import React, { lazy, memo, Suspense, useCallback } from "react";
import { Route, Routes, Navigate, useParams, useLocation } from "react-router-dom";
import { RequireAuth } from "./RequireAuth";
import { routeConfig } from "../config/routeConfig";
import { AppRoutesProps } from "@/shared/types/router";

const AboutOgtPage = lazy(() =>
    import("@/pages/AboutOgtPage").then((m) => ({ default: m.AboutOgtPage })),
);

// Lazy so livekit-client is not in the main bundle: only the egress browser and
// viewers who actually turn a camera on ever download it.
const LiveEgressLayoutPage = lazy(() =>
    import("@/pages/LiveEgressLayoutPage/ui/LiveEgressLayoutPage").then((m) => ({
        default: m.LiveEgressLayoutPage,
    })),
);

// Locales with a full translation of AboutOgtPage get the same homepage,
// just translated, instead of a redirect to the older ogt-main-page.
const ABOUT_OGT_LOCALES = ["en", "ru"];

const LangIndexRoute = () => {
    const { lang } = useParams<{ lang: string }>();

    if (lang && ABOUT_OGT_LOCALES.includes(lang)) {
        return (
            <Suspense fallback={""}>
                <AboutOgtPage />
            </Suspense>
        );
    }

    return <Navigate to="ogt-main-page" replace />;
};

// Catches URLs missing the required /:lang/ prefix (e.g. old referral
// links shared before the lang segment was added) and redirects to the
// same path under /en, preserving query params like ?ref=.
const MissingLangRedirect = () => {
    const location = useLocation();
    return (
        <Navigate
            to={`/en${location.pathname}${location.search}`}
            replace
        />
    );
};

const AppRouter = () => {
    const renderRoute = useCallback(
        (route: AppRoutesProps, parentAuthOnly?: boolean) => {
            const isAuthOnly = route.authOnly ?? parentAuthOnly;
            const element = <Suspense fallback={""}>{route.element}</Suspense>;
            const wrappedElement = isAuthOnly ? (
                <RequireAuth>{element}</RequireAuth>
            ) : (
                element
            );

            // Handle index routes
            if (route.index) {
                return <Route key="index" index element={wrappedElement} />;
            }

            // Handle routes with children (nested routes)
            if (route.children && route.children.length > 0) {
                return (
                    <Route
                        key={route.path}
                        path={route.path?.replace(/^\//, "")}
                        element={wrappedElement}
                    >
                        {route.children.map((child) =>
                            renderRoute(child, isAuthOnly),
                        )}
                    </Route>
                );
            }

            // Regular routes without children
            return (
                <Route
                    key={route.path}
                    path={route.path?.replace(/^\//, "")}
                    element={wrappedElement}
                />
            );
        },
        [],
    );

    return (
        <Routes>
            <Route
                path="/"
                element={
                    <Suspense fallback={""}>
                        <AboutOgtPage />
                    </Suspense>
                }
            />
            {/* The hosted layout LiveKit's room-composite egress renders.
                Deliberately OUTSIDE the :lang tree: it is not a user-facing
                page, it takes no locale, and a language redirect would break
                the recording. Chrome-free by design — see the page for why it
                must render no captions. */}
            <Route
                path="/live-egress-layout"
                element={
                    <Suspense fallback={""}>
                        <LiveEgressLayoutPage />
                    </Suspense>
                }
            />
            <Route path=":lang">
                <Route index element={<LangIndexRoute />} />
                {Object.values(routeConfig).map((route) => renderRoute(route))}
            </Route>
            <Route path="*" element={<MissingLangRedirect />} />
        </Routes>
    );
};

export default memo(AppRouter);
